import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/Toast';
import {
  FileSignature, Plus, Save, Printer, Trash2, Pencil, Loader2, X, ChevronDown, RefreshCw, MessageCircle, Eye, CheckCircle2,
} from 'lucide-react';

const STATUS_BADGE = {
  draft: { label: 'Rascunho', class: 'bg-slate-200 text-slate-700' },
  sent: { label: 'Enviada', class: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Aceita', class: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Recusada', class: 'bg-rose-100 text-rose-700' },
};

const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const safe = (v) => (v == null ? '' : String(v).replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c])));

export default function ProposalsPage() {
  const toast = useToast();
  const [proposals, setProposals] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [company, setCompany] = useState({});
  const [tenant, setTenant] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [defaultClauses, setDefaultClauses] = useState('');

  const blankProposal = useCallback(() => ({
    id: null,
    title: '',
    customer_id: '',
    items: [],
    payment_terms: '',
    validity_days: 30,
    delivery_days: 0,
    daily_penalty: '',
    notes: '',
    contract_clauses: defaultClauses,
    status: 'draft',
  }), [defaultClauses]);

  const [form, setForm] = useState(blankProposal());

  const DRAFT_KEY = 'nfce_proposal_draft_v1';

  const draftHasContent = (d) => !!(d && ((d.title && d.title.trim()) || (d.items && d.items.length > 0) || d.customer_id));

  // Salva rascunho automático de propostas NOVAS enquanto o usuário preenche,
  // para nunca perder o trabalho caso o modal feche ou a página recarregue.
  useEffect(() => {
    if (!showForm || form.id) return;
    if (draftHasContent(form)) {
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch (e) { /* noop */ }
    }
  }, [form, showForm]);

  // Recarrega SÓ a lista de produtos — usado ao abrir modal ou botão "atualizar".
  // Evita bug em que produto recém-cadastrado não aparece porque a lista foi
  // carregada no useEffect inicial e ficou em cache no state.
  const reloadProducts = useCallback(async () => {
    try {
      const res = await api.get('/products');
      const list = Array.isArray(res.data) ? res.data : (res.data?.products || []);
      setProducts(list);
      return list;
    } catch (e) {
      return null;
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes, prRes, coRes, tRes, dcRes] = await Promise.all([
        api.get('/proposals').catch(() => ({ data: [] })),
        api.get('/customers').catch(() => ({ data: [] })),
        api.get('/products').catch(() => ({ data: [] })),
        api.get('/settings/company').catch(() => ({ data: {} })),
        api.get('/auth/me').catch(() => ({ data: {} })),
        api.get('/proposals/default-clauses').catch(() => ({ data: { clauses: '' } })),
      ]);
      setProposals(Array.isArray(pRes.data) ? pRes.data : []);
      setCustomers(Array.isArray(cRes.data) ? cRes.data : (cRes.data?.customers || []));
      setProducts(Array.isArray(prRes.data) ? prRes.data : (prRes.data?.products || []));
      setCompany(coRes.data || {});
      setTenant(tRes.data || {});
      setDefaultClauses(dcRes.data?.clauses || '');
    } catch (e) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openCreate = () => {
    // Recarrega produtos ao abrir o modal — garante que itens
    // recém-cadastrados apareçam sem precisar dar F5 na página.
    reloadProducts();
    let draft = null;
    try { draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); } catch (e) { draft = null; }
    if (draftHasContent(draft) && window.confirm('Encontramos uma proposta que você não terminou. Deseja continuar de onde parou?')) {
      setForm({ ...blankProposal(), ...draft, id: null });
    } else {
      try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* noop */ }
      setForm({ ...blankProposal(), contract_clauses: defaultClauses });
    }
    setShowForm(true);
  };

  const openEdit = async (proposalId) => {
    // Recarrega produtos em background — mesmo motivo do openCreate.
    reloadProducts();
    try {
      const { data } = await api.get(`/proposals/${proposalId}`);
      setForm({
        id: data.id,
        title: data.title || '',
        customer_id: data.customer_id || '',
        // Preserva o snapshot já gravado para que o backend não o anule no PUT
        customer_snapshot: data.customer_snapshot || null,
        items: (data.items || []).map(it => ({
          product_id: it.product_id || null,
          description: it.description || '',
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
          discount: Number(it.discount) || 0,
          measurement_type: it.measurement_type || 'unit',
          width: it.width ?? '',
          height: it.height ?? '',
        })),
        payment_terms: data.payment_terms || '',
        validity_days: data.validity_days || 30,
        delivery_days: data.delivery_days || 0,
        daily_penalty: data.daily_penalty || '',
        notes: data.notes || '',
        contract_clauses: data.contract_clauses || defaultClauses,
        status: data.status || 'draft',
      });
      setShowForm(true);
    } catch (e) {
      toast.error('Erro ao abrir proposta');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta proposta?')) return;
    try {
      await api.delete(`/proposals/${id}`);
      toast.success('Proposta excluída');
      loadAll();
    } catch (e) {
      toast.error('Erro ao excluir');
    }
  };

  const handleAddProduct = (product) => {
    setForm(f => ({
      ...f,
      items: [
        ...f.items,
        {
          product_id: product.id,
          description: product.name,
          quantity: 1,
          unit_price: Number(product.price || 0),
          discount: 0,
          measurement_type: 'unit',
          width: '',
          height: '',
        },
      ],
    }));
    setProductSearch('');
    setShowProductDropdown(false);
  };

  const handleAddCustomItem = () => {
    setForm(f => ({
      ...f,
      items: [...f.items, { product_id: null, description: '', quantity: 1, unit_price: 0, discount: 0, measurement_type: 'unit', width: '', height: '' }],
    }));
  };

  const updateItem = (idx, field, value) => {
    setForm(f => {
      const items = [...f.items];
      const numFields = ['quantity', 'unit_price', 'discount', 'width', 'height'];
      const newVal = numFields.includes(field)
        ? (value === '' ? '' : parseFloat(value) || 0)
        : value;
      items[idx] = { ...items[idx], [field]: newVal };
      // Quando largura/altura mudam em modo área, recalcula a quantidade (m²)
      if (items[idx].measurement_type === 'area' && (field === 'width' || field === 'height')) {
        const w = Number(items[idx].width) || 0;
        const h = Number(items[idx].height) || 0;
        items[idx].quantity = +(w * h).toFixed(4);
      }
      // Ao alternar tipo, ajusta valores
      if (field === 'measurement_type') {
        if (value === 'area') {
          const w = Number(items[idx].width) || 0;
          const h = Number(items[idx].height) || 0;
          items[idx].quantity = +(w * h).toFixed(4);
        }
      }
      return { ...f, items };
    });
  };

  const removeItem = (idx) => {
    setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const totals = form.items.reduce((acc, it) => {
    const isArea = it.measurement_type === 'area';
    const qty = isArea ? (Number(it.width) || 0) * (Number(it.height) || 0) : Number(it.quantity || 0);
    const sub = qty * Number(it.unit_price || 0);
    const disc = Number(it.discount || 0);
    acc.subtotal += sub;
    acc.discount += disc;
    acc.total += (sub - disc);
    return acc;
  }, { subtotal: 0, discount: 0, total: 0 });

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Informe o nome da proposta');
      return;
    }
    if (form.items.length === 0) {
      toast.error('Adicione ao menos um item');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: form.items.map(it => ({
          product_id: it.product_id || null,
          description: it.description || '',
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
          discount: Number(it.discount) || 0,
          // Preserva os campos de medição por área (m²) que estavam sendo descartados
          measurement_type: it.measurement_type || 'unit',
          width: it.width === '' || it.width == null ? null : Number(it.width),
          height: it.height === '' || it.height == null ? null : Number(it.height),
        })),
      };
      if (form.id) {
        await api.put(`/proposals/${form.id}`, payload);
        toast.success('Proposta atualizada');
      } else {
        await api.post('/proposals', payload);
        toast.success('Proposta criada');
      }
      try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* noop */ }
      setShowForm(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = (proposal) => {
    const cust = proposal.customer_snapshot || {};
    // Monta endereço completo a partir dos campos disponíveis no snapshot.
    const enderecoFromParts = [
      cust.logradouro || cust.address,
      cust.numero ? `nº ${cust.numero}` : '',
      cust.complemento,
      cust.bairro,
      [cust.cidade, cust.uf].filter(Boolean).join(' - '),
      cust.cep ? `CEP ${cust.cep}` : '',
    ].filter(Boolean).join(', ');
    const itemsHtml = (proposal.items || []).map((it, i) => {
      const isArea = it.measurement_type === 'area';
      const w = Number(it.width) || 0;
      const h = Number(it.height) || 0;
      const m2 = +(w * h).toFixed(4);
      const qty = isArea ? m2 : Number(it.quantity || 0);
      const sub = qty * Number(it.unit_price || 0);
      const final = sub - Number(it.discount || 0);
      const qtyLabel = isArea
        ? `${w.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} × ${h.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} m = ${m2.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} m²`
        : qty.toLocaleString('pt-BR');
      return `<tr>
        <td class="num">${i + 1}</td>
        <td>${safe(it.description)}</td>
        <td>${qtyLabel}</td>
        <td class="num">${brl(it.unit_price)}${isArea ? '/m²' : ''}</td>
        <td class="num">${brl(it.discount)}</td>
        <td class="num">${brl(final)}</td>
      </tr>`;
    }).join('');
    const proposalTotals = (proposal.items || []).reduce((acc, it) => {
      const isArea = it.measurement_type === 'area';
      const qty = isArea ? (Number(it.width) || 0) * (Number(it.height) || 0) : Number(it.quantity || 0);
      const sub = qty * Number(it.unit_price || 0);
      const disc = Number(it.discount || 0);
      acc.subtotal += sub;
      acc.discount += disc;
      acc.total += (sub - disc);
      return acc;
    }, { subtotal: 0, discount: 0, total: 0 });

    const validity = proposal.validity_days || 30;
    const deliveryDays = proposal.delivery_days || 0;
    const clauses = (proposal.contract_clauses || defaultClauses || '')
      .replace('{VALIDADE}', String(validity))
      .replace('{PRAZO_ENTREGA}', deliveryDays > 0 ? String(deliveryDays) : '____')
      .replace('{MULTA_DIARIA}', proposal.daily_penalty ? String(proposal.daily_penalty) : '____');
    const createdAt = proposal.created_at ? new Date(proposal.created_at).toLocaleDateString('pt-BR') : new Date().toLocaleDateString('pt-BR');
    const logo = tenant?.logo || company?.logo || '';

    const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8"/>
<title>${safe(proposal.title || 'Proposta')}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #111; margin: 18mm; font-size: 12px; line-height: 1.4; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  h2 { font-size: 14px; margin: 14px 0 6px; text-transform: uppercase; letter-spacing: .5px; color: #555; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; padding-bottom: 12px; border-bottom: 2px solid #333; }
  .header img { max-height: 70px; max-width: 180px; object-fit: contain; }
  .meta { font-size: 11px; color: #444; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
  th { background: #f3f3f3; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .totals { width: 320px; margin-left: auto; margin-top: 6px; }
  .totals tr td { border: none; padding: 2px 6px; }
  .totals tr td:last-child { text-align: right; }
  .totals .grand { font-size: 14px; font-weight: 700; border-top: 2px solid #000; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .box { border: 1px solid #e3e3e3; border-radius: 6px; padding: 8px 10px; background: #fafafa; }
  .clauses { white-space: pre-wrap; font-size: 11px; line-height: 1.5; }
  .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 60px; }
  .signatures .line { border-top: 1px solid #000; padding-top: 4px; text-align: center; font-size: 11px; }
  .footer { margin-top: 16px; font-size: 10px; color: #888; text-align: center; }
  @media print { body { margin: 12mm; } .no-print { display: none !important; } }
</style></head>
<body>
  <div class="header">
    <div>
      ${logo ? `<img src="${safe(logo)}" alt="Logo"/><br/>` : ''}
      <h1>${safe((company.razao_social || company.nome_fantasia || 'EMPRESA').toUpperCase())}</h1>
      <div class="meta">
        ${company.nome_fantasia && company.razao_social && company.nome_fantasia !== company.razao_social ? safe(company.nome_fantasia) + '<br/>' : ''}
        ${company.cnpj ? `CNPJ: ${safe(company.cnpj)}` : ''} ${company.inscricao_estadual ? ` | IE: ${safe(company.inscricao_estadual)}` : ''}<br/>
        ${[company.endereco || company.logradouro, company.numero ? 'nº ' + company.numero : '', company.bairro].filter(Boolean).map(safe).join(', ')}<br/>
        ${[company.cidade, company.uf].filter(Boolean).map(safe).join(' - ')} ${company.cep ? 'CEP ' + safe(company.cep) : ''}<br/>
        ${company.telefone ? 'Tel: ' + safe(company.telefone) : ''} ${company.email ? ' | ' + safe(company.email) : ''}
      </div>
    </div>
    <div style="text-align:right">
      <h2 style="margin:0">PROPOSTA COMERCIAL</h2>
      <div class="meta">Nº ${safe((proposal.id || '').slice(0, 8).toUpperCase())}</div>
      <div class="meta">Data: ${safe(createdAt)}</div>
      <div class="meta">Validade: ${validity} dias</div>
    </div>
  </div>

  <h2>${safe(proposal.title)}</h2>

  <div class="grid">
    <div class="box">
      <strong>CLIENTE</strong><br/>
      ${safe(cust.name || '—')}<br/>
      ${cust.cpf_cnpj ? `CPF/CNPJ: ${safe(cust.cpf_cnpj)}<br/>` : ''}
      ${cust.email ? `E-mail: ${safe(cust.email)}<br/>` : ''}
      ${cust.phone ? `Telefone: ${safe(cust.phone)}<br/>` : ''}
      ${enderecoFromParts ? `Endereço: ${safe(enderecoFromParts)}` : ''}
    </div>
    <div class="box">
      <strong>CONDIÇÕES</strong><br/>
      ${proposal.payment_terms ? `Pagamento: ${safe(proposal.payment_terms)}<br/>` : ''}
      Validade desta proposta: ${validity} dias<br/>
      ${deliveryDays > 0 ? `Prazo de entrega da obra: ${deliveryDays} dias (dias de chuva não contados)<br/>` : ''}
      Status: ${safe((STATUS_BADGE[proposal.status] || { label: proposal.status }).label)}
    </div>
  </div>

  <h2>Itens</h2>
  <table>
    <thead><tr>
      <th style="width:30px">#</th>
      <th>Descrição</th>
      <th style="width:60px" class="num">Qtd</th>
      <th style="width:90px" class="num">Vl. Unit.</th>
      <th style="width:80px" class="num">Desc.</th>
      <th style="width:100px" class="num">Total</th>
    </tr></thead>
    <tbody>${itemsHtml || '<tr><td colspan="6" class="num" style="text-align:center;color:#888">Nenhum item</td></tr>'}</tbody>
  </table>

  <table class="totals">
    <tr><td>Subtotal</td><td>${brl(proposalTotals.subtotal)}</td></tr>
    ${proposalTotals.discount > 0 ? `<tr><td>Descontos</td><td>- ${brl(proposalTotals.discount)}</td></tr>` : ''}
    <tr class="grand"><td>TOTAL GERAL</td><td>${brl(proposalTotals.total)}</td></tr>
  </table>

  ${proposal.notes ? `<h2>Observações</h2><div class="box">${safe(proposal.notes)}</div>` : ''}

  <h2>Minuta de Contrato — Cláusulas e Responsabilidades</h2>
  <div class="clauses">${safe(clauses)}</div>

  <div class="signatures">
    <div class="line">${safe((company.razao_social || company.nome_fantasia || 'CONTRATADA'))}<br/><span style="font-size:10px">CONTRATADA</span></div>
    <div class="line">${safe(cust.name || 'CLIENTE')}<br/><span style="font-size:10px">CONTRATANTE</span></div>
  </div>

  <div class="footer">Documento gerado em ${new Date().toLocaleString('pt-BR')}</div>

  <script>window.addEventListener('load', () => setTimeout(() => window.print(), 250));</script>
</body></html>`;

    const w = window.open('', '_blank', 'width=900,height=900');
    if (!w) { toast.error('Pop-up bloqueado. Permita pop-ups deste site.'); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  const [shareModal, setShareModal] = useState(null);
  const [sharePhone, setSharePhone] = useState('');
  const [sharing, setSharing] = useState(false);

  const handleShare = async (p) => {
    setSharing(true);
    try {
      const { data } = await api.post(`/proposals/${p.id}/share`);
      const url = `${window.location.origin}/proposta/${data.public_token}`;
      setShareModal({ ...data, url });
      setSharePhone(data.customer_phone || '');
      loadAll();
    } catch (e) {
      toast.error('Erro ao gerar link da proposta');
    } finally {
      setSharing(false);
    }
  };

  const sendWhatsApp = () => {
    if (!shareModal) return;
    let digits = (sharePhone || '').replace(/\D/g, '');
    if (digits && digits.length <= 11 && !digits.startsWith('55')) digits = '55' + digits;
    const msg =
      `Olá${shareModal.customer_name ? ' ' + shareModal.customer_name : ''}! ` +
      `Segue a proposta *${shareModal.title}*` +
      (shareModal.total ? ` no valor de ${brl(shareModal.total)}` : '') + '.\n\n' +
      `Acesse pelo link: ${shareModal.url}\n\n` +
      `Validade: ${shareModal.validity_days} dias.`;
    const base = digits ? `https://wa.me/${digits}` : 'https://wa.me/';
    window.open(`${base}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareModal.url);
      toast.success('Link copiado!');
    } catch (e) {
      toast.error('Não foi possível copiar');
    }
  };

  // Filter products by search
  const productResults = productSearch.trim().length === 0
    ? products.slice(0, 8)
    : products.filter(p =>
        (p.name || '').toLowerCase().includes(productSearch.toLowerCase()) ||
        (p.barcode || '').includes(productSearch)
      ).slice(0, 12);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="container mx-auto p-4 md:p-6" data-testid="proposals-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileSignature className="w-6 h-6 text-violet-600" /> Propostas de Serviço</h1>
          <p className="text-sm text-slate-500">Crie propostas comerciais com minuta de contrato</p>
        </div>
        <Button onClick={openCreate} className="bg-violet-600 hover:bg-violet-700" data-testid="new-proposal-btn">
          <Plus className="w-4 h-4 mr-2" /> Nova proposta
        </Button>
      </div>

      {(() => {
        const acceptedCount = proposals.filter(p => p.status === 'accepted').length;
        const viewedCount = proposals.filter(p => (p.views_count || 0) > 0 && p.status !== 'accepted').length;
        if (acceptedCount === 0 && viewedCount === 0) return null;
        return (
          <div className="flex flex-wrap gap-3 mb-4" data-testid="proposal-activity-banner">
            {acceptedCount > 0 && (
              <div className="flex items-center gap-2 text-sm bg-green-50 border border-green-200 text-green-700 rounded-lg px-3 py-2">
                <CheckCircle2 className="w-4 h-4" /> {acceptedCount} proposta(s) aceita(s) pelo cliente
              </div>
            )}
            {viewedCount > 0 && (
              <div className="flex items-center gap-2 text-sm bg-blue-50 border border-blue-200 text-blue-700 rounded-lg px-3 py-2">
                <Eye className="w-4 h-4" /> {viewedCount} proposta(s) já visualizada(s) pelo cliente
              </div>
            )}
          </div>
        );
      })()}

      <Card>
        <CardHeader>
          <CardTitle>Propostas cadastradas</CardTitle>
          <CardDescription>{proposals.length} proposta(s)</CardDescription>
        </CardHeader>
        <CardContent>
          {proposals.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <FileSignature className="w-12 h-12 mx-auto mb-3 opacity-30" />
              Nenhuma proposta criada. Clique em &quot;Nova proposta&quot; para começar.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Itens</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Criada em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {proposals.map(p => {
                  const total = (p.items || []).reduce((s, it) => s + (Number(it.quantity || 0) * Number(it.unit_price || 0) - Number(it.discount || 0)), 0);
                  const sb = STATUS_BADGE[p.status] || STATUS_BADGE.draft;
                  return (
                    <TableRow key={p.id} data-testid={`proposal-row-${p.id}`}>
                      <TableCell className="font-medium">{p.title}</TableCell>
                      <TableCell>{p.customer_snapshot?.name || '—'}</TableCell>
                      <TableCell>{(p.items || []).length}</TableCell>
                      <TableCell className="font-semibold">{brl(total)}</TableCell>
                      <TableCell>
                    <Badge className={sb.class}>{sb.label}</Badge>
                    {p.status === 'accepted' && p.accepted_at ? (
                      <div className="text-[11px] text-green-600 mt-1 flex items-center gap-1" data-testid={`proposal-accepted-${p.id}`}>
                        <CheckCircle2 className="w-3 h-3" /> Aceita em {new Date(p.accepted_at).toLocaleDateString('pt-BR')}{p.accepted_name ? ` por ${p.accepted_name}` : ''}
                      </div>
                    ) : (p.views_count || 0) > 0 ? (
                      <div className="text-[11px] text-blue-600 mt-1 flex items-center gap-1" data-testid={`proposal-viewed-${p.id}`}>
                        <Eye className="w-3 h-3" /> Vista {p.views_count}x{p.last_viewed_at ? ` • ${new Date(p.last_viewed_at).toLocaleDateString('pt-BR')}` : ''}
                      </div>
                    ) : p.status === 'sent' ? (
                      <div className="text-[11px] text-slate-400 mt-1">Aguardando visualização</div>
                    ) : null}
                  </TableCell>
                      <TableCell>{p.created_at ? new Date(p.created_at).toLocaleDateString('pt-BR') : '—'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleShare(p)} disabled={sharing} title="Enviar por WhatsApp" data-testid={`share-proposal-${p.id}`}>
                            <MessageCircle className="w-4 h-4 text-green-600" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handlePrint(p)} title="Imprimir / PDF" data-testid={`print-proposal-${p.id}`}>
                            <Printer className="w-4 h-4 text-violet-600" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(p.id)} title="Editar" data-testid={`edit-proposal-${p.id}`}>
                            <Pencil className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)} title="Excluir" data-testid={`delete-proposal-${p.id}`}>
                            <Trash2 className="w-4 h-4 text-rose-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal de criar/editar (renderizado inline) */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-auto p-4" onClick={() => { if (draftHasContent(form) || form.id) { toast.info('Use o botão X ou Cancelar para fechar (sua proposta está protegida).'); return; } setShowForm(false); }}>
          <div className="bg-white rounded-lg max-w-4xl w-full my-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{form.id ? 'Editar proposta' : 'Nova proposta'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)} data-testid="close-proposal-form"><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-4 space-y-4 max-h-[80vh] overflow-y-auto">
              {/* Identificação */}
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Nome da proposta *</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Ex: Implantação do sistema PDV"
                    data-testid="proposal-title-input"
                  />
                </div>
                <div>
                  <Label>Cliente</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.customer_id}
                    onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                    data-testid="proposal-customer-select"
                  >
                    <option value="">Selecione um cliente cadastrado…</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name} {c.cpf_cnpj ? `(${c.cpf_cnpj})` : ''}</option>)}
                  </select>
                </div>
              </div>

              {/* Itens */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">Itens da proposta</h4>
                  <Button size="sm" variant="outline" onClick={handleAddCustomItem} data-testid="add-custom-item-btn">
                    <Plus className="w-4 h-4 mr-1" /> Item livre
                  </Button>
                </div>

                {/* Search produtos */}
                <div className="relative mb-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Buscar produto cadastrado por nome ou código de barras..."
                      value={productSearch}
                      onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                      onFocus={() => setShowProductDropdown(true)}
                      data-testid="proposal-product-search"
                      className="flex-1"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      type="button"
                      onClick={async () => {
                        const list = await reloadProducts();
                        if (Array.isArray(list)) {
                          toast.success(`${list.length} produto(s) carregado(s)`);
                        } else {
                          toast.error('Erro ao atualizar produtos');
                        }
                      }}
                      data-testid="reload-products-btn"
                      title="Atualizar lista de produtos"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {products.length > 0 && `${products.length} produto(s) disponível(is). `}
                    Digite para filtrar. Não encontrou? Clique em <RefreshCw className="w-3 h-3 inline" /> para atualizar.
                  </p>
                  {showProductDropdown && productResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow max-h-60 overflow-auto">
                      {productResults.map(p => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => handleAddProduct(p)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 flex justify-between"
                          data-testid={`add-product-${p.id}`}
                        >
                          <span>{p.name}</span>
                          <span className="text-slate-500">{brl(p.price)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {showProductDropdown && productSearch.trim() && productResults.length === 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow p-3 text-sm text-slate-500">
                      Nenhum produto encontrado para &quot;{productSearch}&quot;. Se acabou de cadastrar, clique no botão <RefreshCw className="w-3 h-3 inline" /> ao lado.
                    </div>
                  )}
                </div>

                {form.items.length === 0 ? (
                  <p className="text-sm text-slate-400 italic text-center py-4">Adicione itens buscando produtos cadastrados ou criando itens livres.</p>
                ) : (
                  <div className="space-y-2">
                    {form.items.map((it, idx) => {
                      const isArea = it.measurement_type === 'area';
                      const w = Number(it.width) || 0;
                      const h = Number(it.height) || 0;
                      const m2 = +(w * h).toFixed(4);
                      return (
                      <div key={idx} className="border rounded-md p-2 bg-slate-50/50" data-testid={`proposal-item-${idx}`}>
                        <div className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-5">
                            <Label className="text-xs">Descrição</Label>
                            <Input value={it.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} />
                          </div>
                          <div className="col-span-3">
                            <Label className="text-xs">Cálculo</Label>
                            <select
                              className="flex h-10 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
                              value={it.measurement_type || 'unit'}
                              onChange={(e) => updateItem(idx, 'measurement_type', e.target.value)}
                              data-testid={`item-measurement-type-${idx}`}
                            >
                              <option value="unit">Por unidade</option>
                              <option value="area">Por m² (largura × altura)</option>
                            </select>
                          </div>
                          {isArea ? (
                            <div className="col-span-3 grid grid-cols-3 gap-1">
                              <div>
                                <Label className="text-[10px]">Larg. (m)</Label>
                                <Input type="number" step="0.01" min="0" value={it.width ?? ''} onChange={(e) => updateItem(idx, 'width', e.target.value)} data-testid={`item-width-${idx}`} />
                              </div>
                              <div>
                                <Label className="text-[10px]">Alt. (m)</Label>
                                <Input type="number" step="0.01" min="0" value={it.height ?? ''} onChange={(e) => updateItem(idx, 'height', e.target.value)} data-testid={`item-height-${idx}`} />
                              </div>
                              <div>
                                <Label className="text-[10px]">m²</Label>
                                <Input type="number" readOnly value={m2 || 0} className="bg-slate-100" data-testid={`item-area-${idx}`} />
                              </div>
                            </div>
                          ) : (
                            <div className="col-span-3">
                              <Label className="text-xs">Qtd</Label>
                              <Input type="number" step="0.001" min="0" value={it.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} />
                            </div>
                          )}
                          <div className="col-span-1 flex justify-end">
                            <Button size="icon" variant="ghost" onClick={() => removeItem(idx)} className="text-red-500"><X className="w-4 h-4" /></Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-12 gap-2 mt-2">
                          <div className="col-span-3">
                            <Label className="text-xs">Vl. Unit. {isArea && <span className="text-slate-500">(por m²)</span>}</Label>
                            <Input type="number" step="0.01" min="0" value={it.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} />
                          </div>
                          <div className="col-span-3">
                            <Label className="text-xs">Desconto</Label>
                            <Input type="number" step="0.01" min="0" value={it.discount} onChange={(e) => updateItem(idx, 'discount', e.target.value)} />
                          </div>
                          <div className="col-span-6 flex items-end justify-end pr-1">
                            <span className="text-sm">
                              {isArea && <span className="text-slate-500 mr-2">{m2.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} m² × {brl(it.unit_price)} =</span>}
                              <strong>{brl((isArea ? m2 : Number(it.quantity || 0)) * Number(it.unit_price || 0) - Number(it.discount || 0))}</strong>
                            </span>
                          </div>
                        </div>
                      </div>
                    );})}
                    <div className="text-right text-sm pt-2 border-t mt-2">
                      Subtotal: <strong>{brl(totals.subtotal)}</strong>
                      {totals.discount > 0 && <> | Descontos: <strong>- {brl(totals.discount)}</strong></>}
                      &nbsp;&nbsp;|&nbsp;&nbsp;
                      <span className="text-base font-bold text-emerald-700">TOTAL: {brl(totals.total)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Condições */}
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <Label>Condições de pagamento</Label>
                  <Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="Ex: 50% na assinatura + 50% em 30 dias" />
                </div>
                <div>
                  <Label>Validade (dias)</Label>
                  <Input type="number" min="1" value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: parseInt(e.target.value) || 30 })} />
                </div>
                <div>
                  <Label>Prazo de entrega da obra (dias)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.delivery_days}
                    onChange={(e) => setForm({ ...form, delivery_days: parseInt(e.target.value) || 0 })}
                    placeholder="Ex: 60"
                    data-testid="proposal-delivery-days"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">Dias de chuva não são contados</p>
                </div>
              </div>

              <div>
                <Label>Multa por dia de atraso</Label>
                <Input
                  value={form.daily_penalty}
                  onChange={(e) => setForm({ ...form, daily_penalty: e.target.value })}
                  placeholder="Ex: R$ 200,00 ou 0,5% do valor do contrato"
                  data-testid="proposal-daily-penalty"
                />
                <p className="text-[11px] text-slate-400 mt-1">Só se aplica quando não houver chuva, mau tempo ou fenômenos naturais (esses apenas prorrogam o prazo).</p>
              </div>

              <div>
                <Label>Observações</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notas adicionais para o cliente" />
              </div>

              {/* Cláusulas */}
              <details className="border rounded-lg" open>
                <summary className="cursor-pointer p-3 font-semibold flex items-center gap-2">
                  <ChevronDown className="w-4 h-4" /> Minuta de contrato — cláusulas e responsabilidades
                </summary>
                <div className="p-3 pt-0 space-y-2">
                  <p className="text-xs text-slate-500">Edite as cláusulas como preferir. Use <code className="bg-slate-100 px-1">{'{VALIDADE}'}</code> para os dias de validade e <code className="bg-slate-100 px-1">{'{PRAZO_ENTREGA}'}</code> para o prazo de entrega da obra automaticamente.</p>
                  <Textarea
                    rows={14}
                    className="font-mono text-xs"
                    value={form.contract_clauses}
                    onChange={(e) => setForm({ ...form, contract_clauses: e.target.value })}
                    data-testid="proposal-clauses-textarea"
                  />
                  <Button size="sm" variant="outline" onClick={() => setForm({ ...form, contract_clauses: defaultClauses })}>Restaurar padrão</Button>
                </div>
              </details>

              {/* Status */}
              <div className="flex items-center gap-3">
                <Label>Status:</Label>
                <select
                  className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  data-testid="proposal-status-select"
                >
                  <option value="draft">Rascunho</option>
                  <option value="sent">Enviada</option>
                  <option value="accepted">Aceita</option>
                  <option value="rejected">Recusada</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="save-proposal-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Salvar proposta
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de compartilhamento por WhatsApp */}
      {shareModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShareModal(null)}>
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2"><MessageCircle className="w-5 h-5 text-green-600" /> Enviar proposta</h3>
              <Button variant="ghost" size="sm" onClick={() => setShareModal(null)} data-testid="close-share-modal"><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <Label>WhatsApp do cliente</Label>
                <Input
                  value={sharePhone}
                  onChange={(e) => setSharePhone(e.target.value)}
                  placeholder="Ex: (11) 99999-9999"
                  data-testid="share-phone-input"
                />
                <p className="text-[11px] text-slate-400 mt-1">Confira o número. Se não tiver DDI, o 55 (Brasil) é adicionado automaticamente.</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <Label className="text-xs text-slate-500">Link público da proposta (expira em {shareModal.validity_days} dias)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input readOnly value={shareModal.url} className="text-xs" data-testid="share-link-input" />
                  <Button variant="outline" size="sm" onClick={copyShareLink} data-testid="copy-share-link">Copiar</Button>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setShareModal(null)}>Fechar</Button>
              <Button onClick={sendWhatsApp} className="bg-green-600 hover:bg-green-700" data-testid="send-whatsapp-btn">
                <MessageCircle className="w-4 h-4 mr-2" /> Abrir WhatsApp
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
